import { createApi } from "@reduxjs/toolkit/query/react";
import { API_BASE_URL } from "../../utils/constants";
import { axiosBaseQuery } from "../baseQuery";

// parser
import dashboardParser from "../parser/DashboardParser";

// interfaces
import {
  ScoreCardApiRes,
  ScoreCardDataParsedRes,
  ScoreCardApiReqInterface,
  QuarterApiReqInterface,
  QuarterWiseModalsGraphApiRes,
  QuarterWiseModalsParsedRes,
  IndividualIndicatorsApiReq,
  IndividualIndicatorsGraphApiRes,
  IndividualIndicatorsGraphParsedRes,
  RegionWiseModalsGraphApiRes,
  RegionWiseModalsGraphApiReq,
  RegionWiseModalsParsedRes,
  PerformanceTableApiReqInterface,
  PerformanceTableApiRes,
  PerformanceTableParsedRes,
  PerformanceIndicatorsGraphApiRes,
  PerformanceIndicatorsGraphParsedRes,
  PerformanceIndicatorsGraphApiReq,
  HitMapTableApiRes,
  HitMapTableParsedRes,
  HitMapRequestInterface,
} from "../types/dashboard";

export const dashboardApis = createApi({
  reducerPath: "dashboardApis",
  baseQuery: axiosBaseQuery({ baseUrl: API_BASE_URL }),
  tagTypes: [
    "ScoreCards",
    "RegionWiseModals",
    "QuarterWiseMedals",
    "PerformanceIndicatorsTable",
    "PerformanceTable",
    "IndividualTable",
    "HitmapTable",
  ],

  endpoints: (builder) => ({
    getScoreCards: builder.query<
      ScoreCardDataParsedRes,
      ScoreCardApiReqInterface
    >({
      query: (params: ScoreCardApiReqInterface) => ({
        url: "/dashboard",
        method: "GET",
        params: params,
      }),
      providesTags: ["ScoreCards"],
      transformResponse: (
        response: ScoreCardApiRes
      ): ScoreCardDataParsedRes => {
        return dashboardParser.getScoreCardParser(response); // Process the data with dashboardParser
      },
    }),

    getRegionWiseModalsGraph: builder.query<
      RegionWiseModalsParsedRes,
      RegionWiseModalsGraphApiReq
    >({
      query: (params) => ({
        url: "/dashboard/region",
        method: "GET",
        params: params,
      }),
      providesTags: ["RegionWiseModals"],
      transformResponse: (
        response: RegionWiseModalsGraphApiRes
      ): RegionWiseModalsParsedRes => {
        return dashboardParser.getRegionWiseMedalsGraphParser(response);
      },
    }),

    getQuarterWiseMedalsGraph: builder.query<
      QuarterWiseModalsParsedRes[],
      QuarterApiReqInterface
    >({
      query: (params: QuarterApiReqInterface) => ({
        url: "/dashboard/quarter",
        method: "GET",
        params: params,
      }),
      providesTags: ["QuarterWiseMedals"],
      transformResponse: (
        response: QuarterWiseModalsGraphApiRes
      ): QuarterWiseModalsParsedRes[] => {
        return dashboardParser.getQuarterWiseMedalGraphParser(response);
      },
    }),

    getIndividualIndicatorsGraph: builder.query<
      IndividualIndicatorsGraphParsedRes,
      IndividualIndicatorsApiReq
    >({
      query: (params) => ({
        url: "/dashboard/performance",
        method: "GET",
        params: params,
      }),
      providesTags: ["IndividualTable"],
      transformResponse: (
        response: IndividualIndicatorsGraphApiRes
      ): IndividualIndicatorsGraphParsedRes => {
        return dashboardParser.getIndividualIndicatorsGraphParser(response);
      },
    }),

    getPerformanceIndicatorsGraph: builder.query<
      PerformanceIndicatorsGraphParsedRes,
      PerformanceIndicatorsGraphApiReq
    >({
      query: (params) => ({
        url: "/dashboard/performance-indicators",
        method: "GET",
        params: params,
      }),
      providesTags: ["PerformanceIndicatorsTable"],
      transformResponse: (
        response: PerformanceIndicatorsGraphApiRes
      ): PerformanceIndicatorsGraphParsedRes => {
        return dashboardParser.getPerformanceIndicatorsGraphParser(response);
      },
    }),

    getHitMapTable: builder.query<HitMapTableParsedRes, HitMapRequestInterface>(
      {
        query: (params) => ({
          url: "/dashboard/heat-map",
          method: "GET",
          params: params,
        }),
        providesTags: ["HitmapTable"],
        transformResponse: (
          response: HitMapTableApiRes
        ): HitMapTableParsedRes => {
          return dashboardParser.getHeatMapTableParser(response);
        },
      }
    ),

    getPerformanceTable: builder.query<
      PerformanceTableParsedRes,
      PerformanceTableApiReqInterface
    >({
      query: (params) => ({
        url: "/dashboard/details",
        method: "GET",
        params: params,
      }),
      providesTags: ["PerformanceTable"],
      transformResponse: (
        response: PerformanceTableApiRes
      ): PerformanceTableParsedRes => {
        return dashboardParser.getPerformanceTableParser(response);
      },
    }),

    refreshmetalscorecard: builder.mutation<any, any>({
      query: (params: any) => ({
        url: `/dashboard/refresh-metal-score-card`,
        method: "GET",
        params: params,
      }),
      invalidatesTags: [
        "ScoreCards",
        "RegionWiseModals",
        "QuarterWiseMedals",
        "PerformanceIndicatorsTable",
        "PerformanceTable",
        "IndividualTable",
        "HitmapTable",
      ], 
    }),
  }),
});

export const {
  useGetScoreCardsQuery,
  useGetRegionWiseModalsGraphQuery,
  useGetQuarterWiseMedalsGraphQuery,
  useGetIndividualIndicatorsGraphQuery,
  useGetPerformanceIndicatorsGraphQuery,
  useLazyGetPerformanceTableQuery,
  useGetHitMapTableQuery,
  useRefreshmetalscorecardMutation,
} = dashboardApis;
